// Copyright (c) Docugami, Inc. All rights reserved.
import { DOMParser } from '@xmldom/xmldom';
import { IDgWordCanvasProxyCallbacks } from "./worker/worker";
import { DgKnownChunk, DgKnownChunkType, DgParagraphMetadata } from './DgParagraphMetadata';
import { DgXmlNames } from "./DgXmlNames";
import { Logger, LoggerCategory } from 'utils/Logger';
import { WordField } from './WordField';



/*eslint-disable */

// Note: the following const have eslint-disable because they form the whole of the Open-Xml-Sdk for JavaScript API.
// Most modules will use only a subset of the API, but disabling portions of the API leads to friction when writing
// code.  Disabling eslint for these variables removes the error indicating that a variable is assigned a value but
// never used.  If we ever re-write these modules in TypeScript, then the public API will change significantly.

const Enumerable = require("../../../../../utils/linq");
const Ltxml = require('../../../../../utils/ltxml.js');
Ltxml.DOMParser = DOMParser

const openXml = require('../../../../../utils/openxml');

const XAttribute = Ltxml.XAttribute;
const XCData = Ltxml.XCData;
const XComment = Ltxml.XComment;
const XContainer = Ltxml.XContainer;
const XDeclaration = Ltxml.XDeclaration;
const XDocument = Ltxml.XDocument;
const XElement = Ltxml.XElement;
const XName = Ltxml.XName;
const XNamespace = Ltxml.XNamespace;
const XNode = Ltxml.XNode;
const XObject = Ltxml.XObject;
const XProcessingInstruction = Ltxml.XProcessingInstruction;
const XText = Ltxml.XText;
const XEntity = Ltxml.XEntity;
const cast = Ltxml.cast;
const castInt = Ltxml.castInt;

const W = openXml.W;
const W14 = openXml.W14;
const W15 = openXml.W15;
const NN = openXml.NoNamespace;
const wNs = openXml.wNs;
const groupAdjacent = openXml.groupAdjacent;
/*eslint-enable */

const GUID_LENGTH = 32;
const DOCUGAMIidConstantOld = 'DOCUGAMI ';
const DOCUINSEidConstantOld = 'DOCUINSE ';
const DOCUREPLidConstantOld = 'DOCUREPL ';
export const DOCUGAMIidConstant = 'DOCUGAMI|';
const DOCUINSEidConstant = 'DOCUINSE|';
const DOCUREPLidConstant = 'DOCUREPL|';
const DOCUCONSTLEN = 9;

// this method gets the set of known chunks for the document, in its current state.
export function getKnownChunksLocal(ooxml: any, dgml: any):  DgKnownChunk[]
{
    // build a dictionary of every span id, and the element it is on.
    const descendants = dgml
        .descendantsAndSelf()
        .toArray();

    const cachedElementsBySpanId: any = {};
    for (let didx = 0; didx < descendants.length; ++didx)
    {
        const spanId: string = descendants[didx].attribute(DgXmlNames.spanId)?.value as string;
        cachedElementsBySpanId[spanId] = descendants[didx];
    }

    const paragraphs = ooxml
        .descendants(W.p)
        .toArray();

    const knownChunks: DgKnownChunk[] = [];
    const knownChunksDictionary: any = {};
    for (let inCnt = 0; inCnt < paragraphs.length; ++inCnt)
    {
        const para = paragraphs[inCnt];
        const paragraphMetadata = getParagraphMetadataFromPElement(para);
        if (!!paragraphMetadata)
        {
            if (!!paragraphMetadata.Ids)
            {
                const spanIdSplit = paragraphMetadata.Ids.split('|');
                for (let cnt = 0; cnt < spanIdSplit.length; ++cnt)
                {
                    const chunkType = paragraphMetadata.KnownChunkType as DgKnownChunkType;
                    const knownChunk = new DgKnownChunk(chunkType, spanIdSplit[cnt], false);
                    knownChunks.push(knownChunk);
                    knownChunksDictionary[spanIdSplit[cnt]] = knownChunk;
                }
            }
        }
    }

    // for every entry in knownChunks
    //   if it is a Matched entry
    //     look up the spanId in the dictionary, get the element
    //     go up the tree, adding all ancestors to an array
    // go through array, add entries to knownChunks

    for (let kcCnt = 0; kcCnt < knownChunks.length; ++kcCnt)
    {
        if (knownChunks[kcCnt].KnownChunkType === DgKnownChunkType.Matched)
        {
            const spanId = knownChunks[kcCnt].Id;
            const element = cachedElementsBySpanId[spanId];
            if (!!element)
            {
                const ancestors = element.ancestors().toArray();
                for (let anCnt = 0; anCnt < ancestors.length; ++anCnt)
                {
                    const anSpanId = ancestors[anCnt].attribute(DgXmlNames.spanId)?.value;
                    if (!!anSpanId)
                    {
                        if (!knownChunksDictionary[anSpanId])
                        {
                            const knownChunk = new DgKnownChunk(DgKnownChunkType.Matched, anSpanId, true);
                            knownChunks.push(knownChunk);
                            knownChunksDictionary[anSpanId] = knownChunk;
                        }
                    }
                }
            }
        }
    }
    Logger.LogDebug(LoggerCategory.AssistedAuthoring, `getKnownChunksLocal knownChunks.length ${knownChunks.length}`);
    return knownChunks;
}

// this method gets the docugami ID from the markup when retreive OOXML for a single paragraph.
export function getDocugamiIdFromRootElement(paraElementFromWordApi: any): string|null
{
    const docElement = paraElementFromWordApi.descendants(W.document).firstOrDefault();
    const body = docElement.element(W.body);
    return getDocugamiIdFromPElement(body);
}

// this method gets the docugami ID from a w:p element (hence the name) when processing each w:p element
// in a larger context after getting the markup for the entire document.
export function getDocugamiIdFromPElement(paraElement: any)
{
    const fieldInfo: WordField[] = WordField.getFieldsFromParagraph(paraElement);
    if (fieldInfo.length > 0) {
        const fieldToUse = Enumerable.from(fieldInfo).firstOrDefault((fi: WordField) => {
            const instrText = fi.InstrText;
            return instrText.startsWith(DOCUGAMIidConstant) || instrText.startsWith(DOCUINSEidConstant) || instrText.startsWith(DOCUREPLidConstant);
        });
        if (!!fieldToUse) {
            const guidString = fieldToUse.InstrText.substring(DOCUCONSTLEN, DOCUCONSTLEN + GUID_LENGTH);
            return guidString;
        }
    }
    return null;
}

// this method gets the docugami ID from one of an array of w:p element
// in a larger context after getting the markup for the entire document.
export function getDocugamiIdFromContentArray(contentArray: any)
{
    for (let contentCnt = 0; contentCnt < contentArray.length; ++contentCnt)
    {
        const content = contentArray[contentCnt]
        const guidString = getDocugamiIdFromPElement(content);
        if (!!guidString)
        {
            return guidString;
        }
    }
    return null;
}

// this method gets the docugami ID from a w:p element (hence the name) when processing each w:p element
// in a larger context after getting the markup for the entire document.
export function getParagraphMetadataFromPElement(paraElement: any): DgParagraphMetadata|null
{
    const fieldInfo: WordField[] = WordField.getFieldsFromParagraph(paraElement);
    if (fieldInfo.length > 0) {
        const fieldToUse = Enumerable.from(fieldInfo).firstOrDefault((fi: WordField) => {
            const instrText = fi.InstrText;
            return instrText.startsWith(DOCUGAMIidConstant) || instrText.startsWith(DOCUINSEidConstant) || instrText.startsWith(DOCUREPLidConstant);
        });
        if (!!fieldToUse) {
            const guidString = fieldToUse.InstrText.substring(DOCUCONSTLEN, DOCUCONSTLEN + GUID_LENGTH);
            const spanTypeString = fieldToUse.InstrText.substring(0, DOCUCONSTLEN);
            let chunkType: DgKnownChunkType = DgKnownChunkType.Matched;
            if (spanTypeString === DOCUGAMIidConstant) {
                chunkType = DgKnownChunkType.Matched;
            }
            else if (spanTypeString === DOCUINSEidConstant) {
                chunkType = DgKnownChunkType.Inserted;
            }
            else if (spanTypeString === DOCUREPLidConstant) {
                chunkType = DgKnownChunkType.Replaced;
            }
            const idsString = fieldToUse.InstrText.substring(DOCUCONSTLEN + GUID_LENGTH + 1);
            return new DgParagraphMetadata(guidString, chunkType, idsString);
        }
    }
    return null;
}

export function updateDocugamiIdInPElement(para: any, uuid: string)
{
    const fieldInfo: WordField[] = WordField.getFieldsFromParagraph(para);
    if (fieldInfo.length > 0) {
        const fieldToUse = Enumerable.from(fieldInfo).firstOrDefault((fi: WordField) => {
            const instrText = fi.InstrText;
            return instrText.startsWith(DOCUGAMIidConstant) || instrText.startsWith(DOCUINSEidConstant) || instrText.startsWith(DOCUREPLidConstant);
        });
        if (!!fieldToUse) {
            fieldToUse.updateDocugamiId(uuid)
            return;
        }
    }

    const fldSimple = new XElement(W.fldSimple,
        new XAttribute(W.instr,
            DOCUGAMIidConstant + uuid));

    const rPr = para.element(W.rPr);
    if (rPr !== null) {
        rPr.addAfterSelf(fldSimple);
    }
    else {
        const firstRun = para.element(W.r);
        if (firstRun !== null) {
            firstRun.addBeforeSelf(fldSimple);
        }
    }
}

export async function addInsertOrReplaceMetadataWithSpanIdToParagraph(paraElement: any, guidToUse: string|null, insertOrReplaceId: string, insertElseReplace: boolean, proxyCallbacks: IDgWordCanvasProxyCallbacks)
{
    let uuid: string = "";
    if (!!guidToUse)
    {
        uuid = guidToUse;
    }
    else
    {
        uuid = await proxyCallbacks.getGuid();
        uuid = uuid.replace(/-/g, "");
    }
    const constantToUse = insertElseReplace ? DOCUINSEidConstant : DOCUREPLidConstant;
    const fldSimple = new XElement(W.fldSimple,
        new XAttribute(W.instr,
            constantToUse + uuid + "|" + insertOrReplaceId));

    const pPr = paraElement.element(W.pPr);
    if (!pPr)
    {
        paraElement.addFirst(fldSimple);
        return;
    }
    pPr.addAfterSelf(fldSimple);
}

function removeMetadataFromParagraph(para: any, matchedOnly: boolean)
{
    const fieldInfo: WordField[] = WordField.getFieldsFromParagraph(para);
    if (matchedOnly) {
        const fieldsToRemove = Enumerable.from(fieldInfo).where((fi: WordField) => {
            const instrText = fi.InstrText;
            return instrText.startsWith(DOCUGAMIidConstant) ||
                instrText.startsWith(DOCUGAMIidConstantOld);
        })
        .toArray();

        fieldsToRemove.forEach((element: WordField) => {
            element.remove();
        });
    }
    else {
        const fieldsToRemove = Enumerable.from(fieldInfo).where((fi: WordField) => {
            const instrText = fi.InstrText;
            return instrText.startsWith(DOCUGAMIidConstant) ||
                instrText.startsWith(DOCUINSEidConstant) ||
                instrText.startsWith(DOCUREPLidConstant) ||
                instrText.startsWith(DOCUGAMIidConstantOld) ||
                instrText.startsWith(DOCUINSEidConstantOld) ||
                instrText.startsWith(DOCUREPLidConstantOld);
        })
        .toArray();

        fieldsToRemove.forEach((element: WordField) => {
            element.remove();
        });
    }
}

export function addSpanIdToParagraphMetadata(paraElement: any, spanId: string)
{
    const paragraphMetadata = getParagraphMetadataFromPElement(paraElement);
    const paraGuid = paragraphMetadata?.ParagraphGuid;
    const knownChunkType = paragraphMetadata?.KnownChunkType;
    let c: string = '';
    if (knownChunkType === DgKnownChunkType.Inserted) {
        c = DOCUINSEidConstant;
    } else if (knownChunkType === DgKnownChunkType.Replaced) {
        c = DOCUREPLidConstant;
    }
    else {
        c = DOCUGAMIidConstant;
    }

    removeMetadataFromParagraph(paraElement, false);

    const fldSimple = new XElement(W.fldSimple,
        new XAttribute(W.instr,
            c + paraGuid + "|" + spanId));

    const pPr = paraElement.element(W.pPr);
    if (!pPr)
    {
        paraElement.addFirst(fldSimple);
        return;
    }
    pPr.addAfterSelf(fldSimple);
}

export async function removeMetadata(openXml: any, proxyCallbacks: IDgWordCanvasProxyCallbacks)
{
    let documentContainsMetadata = doesDocumentContainMetadata(openXml);
    if (documentContainsMetadata)
    {
        const paragraphs = openXml
            .descendants(W.p)
            .toArray();

        for (let inCnt = 0; inCnt < paragraphs.length; ++inCnt)
        {
            const para = paragraphs[inCnt];
            removeMetadataFromParagraph(para, true);
        }

        proxyCallbacks.bucketTimer(2, "100-est/012-Check-Already-Processed-toString");
        const ooxmlWithoutFields = openXml.toString();

        proxyCallbacks.bucketTimer(1, "812-WORD-insertOoxml-Check-Already-Processed");
        await proxyCallbacks.replaceBodyOoxml(ooxmlWithoutFields);

        proxyCallbacks.bucketTimer(1, "814-WORD-getOoxml-Check-Already-Processed");
        const OOXML2 = await proxyCallbacks.getBodyOoxml();

        proxyCallbacks.bucketTimer(2, "100-est/014-Check-Already-Processed-Ltxml-parse");
        const newOpenXml = XElement.parse(OOXML2);
        return newOpenXml;
    }
    return openXml;
}

function doesDocumentContainMetadata(openXml: any): boolean
{
    const containsMetadata = openXml
            .descendants(W.instrText)
            .any((i: any) => i.value.startsWith(DOCUGAMIidConstant) || i.value.startsWith(DOCUGAMIidConstantOld));
    return containsMetadata;
}
